import { useGetContextPack } from "@/api/studio/contexts/queries";
import { DragonflyIcon } from "@/assets/icons";
import { Error } from "@/components/fallbacks/error-boundaries/error";
import { Button } from "@/components/ui/button";
import { ContextPackDropdown } from "@/features/contexts/components/context-pack-dropdown";
import { ContextSkeleton } from "@/features/contexts/components/context-skeleton";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import ENV from "@/utils/env";
import { isoDateFormat } from "@/utils/format";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";

/* Prop Types */
type ContextRouteProps = {
  contextPackId: string;
};

/*
 * ContextRoute component
 */
export const ContextPackRoute = ({ contextPackId }: ContextRouteProps) => {
  /* State */
  const [assetIndex, setAssetIndex] = useState(0);

  /* Stores */
  const theme = useGlobalStore((state) => state.theme);

  /* Hooks */
  const { segmentPageEvent } = useSegment();

  /* Queries */
  const { data: contextPack, isLoading: isContextPackLoading, isError: isContextPackError } = useGetContextPack(contextPackId);

  /* Handlers */
  const handleContextClick = (index: number) => {
    setAssetIndex(index);
  };

  /* Side effects */
  useEffect(() => {
    // segment page event when a user visits the report in depth page
    if (theme)
      segmentPageEvent("Context Pack", {
        title: "Context Pack | Dragonfly AI Studio",
        theme: theme,
      });
  }, []);

  /* Loading */
  if (isContextPackLoading) {
    return <ContextSkeleton />;
  }

  /* Error */
  if (isContextPackError || !contextPack) {
    return <Error />;
  }

  /* Render */
  return (
    <div className="flex flex-col gap-4 px-7 py-4 min-h-screen max-h-screen">
      <div className="flex justify-between items-center">
        <div className="flex justify-center items-center gap-1">
          <Button variant="ghost" className="shrink-0" onClick={() => window.history.back()}>
            <ChevronLeftIcon className="size-4" />
          </Button>
          <h3 className="pl-4">{contextPack?.name}</h3>
        </div>
        <div className="flex items-center gap-2">
          {contextPack?.isDragonfly && (
            <>
              <DragonflyIcon className="size-4" /> <p className="text-sm mr-4">Dragonfly template</p>
            </>
          )}

          {/* Only show the dropdown if it isn't a dragonfly pack */}
          {!contextPack?.isDragonfly && <ContextPackDropdown contextPack={contextPack} />}
        </div>
      </div>

      <h1>Context {assetIndex + 1}</h1>

      <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
        {/* Context list */}
        <div className="flex flex-col gap-4 overflow-y-auto">
          {contextPack?.contexts.map((context, index) => (
            <div
              className={`flex items-center justify-center size-14 bg-base-200 rounded-lg border-4 border-base-200 cursor-pointer ${index === assetIndex ? "opacity-100 drop-shadow-md" : "opacity-40 hover:opacity-80"}`}
              onClick={() => handleContextClick(index)}
              key={context.id}
            >
              <img src={`${ENV.API_PLATFORM_URL}/v3/contexts/${context.id}/image?maxWidth=1024&maxHeight=1024&showDropzone=true`} alt="" className="h-full w-full object-contain" />
            </div>
          ))}
        </div>

        {/* Context render with dropzone */}
        <div className="grow flex flex-row bg-base-300 rounded-lg overflow-hidden max-h-full relative p-6">
          <img
            src={`${ENV.API_PLATFORM_URL}/v3/contexts/${contextPack?.contexts[assetIndex].id}/image?maxWidth=1024&maxHeight=1024&showDropzone=true`}
            alt="placeholder"
            className="w-full h-full object-contain"
          />
        </div>

        {/* Context pack details */}
        <div className="flex flex-col min-w-[370px] max-w-[20vw] p-4 min-h-full rounded-lg">
          <h2 className="">About this context pack</h2>
          {/* <p className="opacity-70">
            Use this context to test how your assets perform in e-comm digital sites such as Amazon, Tesco, etc. We recommend creating your own context if you have more specific
            industry and/or category you want to test in display study.
          </p> */}
          <div className="flex mt-4 justify-between p-3 border-b border-base-300">
            <p className="text-sm font-semibold">Use case</p>
            <p className="text-sm font-semibold">{contextPack?.useCase.name}</p>
          </div>
          <div className="flex justify-between p-3 border-b border-base-300">
            <p className="text-sm font-semibold">No. of contexts</p>
            <p className="text-sm font-semibold">{contextPack?.contextCount}</p>
          </div>
          <div className="flex justify-between p-3 border-b border-base-300">
            <p className="text-sm font-semibold">Created by</p>
            <p className="text-sm font-semibold">{contextPack?.username}</p>
          </div>
          <div className="flex justify-between p-3 border-b border-base-300">
            <p className="text-sm font-semibold">Created on</p>
            <p className="text-sm font-semibold">{isoDateFormat(contextPack.createdAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
